// W34 / M34 — AI projektfelvétel.
//
// Hang alapján veszünk fel egy projektet: a böngésző beszédfelismerője adja az
// átiratot, az átiratból pedig az AI javasol projektet és feladatokat. Maga az
// AI-hívás NINCS itt — az `onSubmit` visszahívás dolga. Ez a képernyő csak az
// állapotokat vezeti:
//
//   1 készenlét → 2 felvétel → 3 feldolgozás → 4 szerkeszthető előnézet
//                                            ↘ 5 hiba (az átirat megmarad)
//
// A beszédfelismerő nem minden böngészőben van meg (Firefox, több mobil
// böngésző). Ott nem dől el a képernyő: ugyanez a folyamat megy szöveges
// bevitellel — az átirat kézzel is begépelhető.

import { t } from '../lib/i18n';
import { openDialog } from '../lib/dialog';
import { h, icon, icons } from '../lib/dom';
import { selectField, textField, validateAll } from '../lib/fields';
import { priorityLabel, priorityMeta, type TaskPriority } from '../lib/model';
import { priorityTag } from '../lib/components';

// ---------------------------------------------------------------------------
// Szerződés
// ---------------------------------------------------------------------------

export type AiTaskDraft = {
  title: string;
  priority: TaskPriority;
};

export type AiProjectDraft = {
  title: string;
  tasks: AiTaskDraft[];
};

export type AiCaptureState = 'idle' | 'recording' | 'processing' | 'preview' | 'error';

export type AiCapturePageOptions = {
  /// Az átirat feldolgozása. Ez hívja az AI-t — a képernyő csak megvárja.
  /// Hibát dobva a képernyő az 5. (hiba) állapotba kerül, az átirat megmarad.
  onSubmit: (text: string) => Promise<AiProjectDraft>;
  /// A jóváhagyott javaslatból projekt készítése.
  onCreate: (draft: AiProjectDraft) => void;
  /// Elvetéskor hívjuk (a képernyő magától visszaáll készenlétbe).
  onCancel?: () => void;
};

// ---------------------------------------------------------------------------
// Beszédfelismerő — saját, minimális típusokkal
// ---------------------------------------------------------------------------
//
// A `SpeechRecognition` nincs benne a TypeScript DOM-könyvtárában, a globális
// kiegészítés pedig ütközne, ha egy későbbi verzió felveszi. Ezért csak annyit
// írunk le belőle, amennyit használunk, és egyszer castolunk a `window`-on.

type SpeechAlternative = { transcript: string };

type SpeechResult = {
  readonly length: number;
  readonly isFinal: boolean;
  readonly [index: number]: SpeechAlternative | undefined;
};

type SpeechResultList = {
  readonly length: number;
  readonly [index: number]: SpeechResult | undefined;
};

type SpeechResultEvent = { resultIndex: number; results: SpeechResultList };

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

type SpeechWindow = Window & {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
};

/// A beszédfelismerő osztálya, ha a böngésző ismeri.
export function speechRecognitionCtor(): SpeechRecognitionCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as SpeechWindow;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

/// Magyar hibaszövegek a felismerő hibakódjaihoz.
function speechErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return t('ai.micBlocked');
    case 'no-speech':
      return t('ai.noSpeech');
    case 'audio-capture':
      return t('ai.noMic');
    case 'network':
      return t('ai.recognizerUnavailable');
    default:
      return t('ai.recordFailed');
  }
}

function formatClock(milliseconds: number): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const micPath =
  'M8 2.2a1.8 1.8 0 011.8 1.8v3.2a1.8 1.8 0 01-3.6 0V4A1.8 1.8 0 018 2.2zM4.2 7.6a3.8 3.8 0 007.6 0M8 11.6v2.2';
const editPath = 'M11.2 2.6l2.2 2.2-8 8-3 .8.8-3z';
const dropPath = 'M4 4l8 8M12 4l-8 8';

/// A nyers javaslat megtisztítása: üres című feladat nem kerül be, és mindig
/// van prioritás. Az AI kimenete nem megbízható, a képernyő viszont az.
export function normalizeDraft(draft: AiProjectDraft): AiProjectDraft {
  const tasks = Array.isArray(draft.tasks) ? draft.tasks : [];
  return {
    title: typeof draft.title === 'string' ? draft.title.trim() : '',
    tasks: tasks
      .map((task) => ({
        title: typeof task.title === 'string' ? task.title.trim() : '',
        priority: task.priority in priorityMeta ? task.priority : ('mid' as TaskPriority),
      }))
      .filter((task) => task.title.length > 0),
  };
}

const priorityChoices = (Object.keys(priorityMeta) as TaskPriority[]).map((p) => ({
  value: p,
  label: priorityLabel(p),
}));

// ---------------------------------------------------------------------------
// A képernyő
// ---------------------------------------------------------------------------

export function aiCapturePage(options: AiCapturePageOptions): HTMLElement {
  const root = h('div', { class: 'col', style: { gap: '0' } });

  const supported = speechRecognitionCtor() !== undefined;

  let state: AiCaptureState = 'idle';
  /// Szöveges bevitel felvétel helyett (ha nincs felismerő, ez az egyetlen út).
  let manual = !supported;
  let transcript = '';
  let errorMessage = '';
  let draft: AiProjectDraft = { title: '', tasks: [] };

  let recognition: SpeechRecognitionLike | null = null;
  let paused = false;
  let elapsedMs = 0;
  let segmentStartedAt = 0;
  /// Egyszerre csak egy feldolgozás eredménye számít (kétszeri indítás ellen).
  let submitToken = 0;
  let ticker: number | undefined;

  function stopTicker(): void {
    if (ticker !== undefined) {
      window.clearInterval(ticker);
      ticker = undefined;
    }
  }

  function currentElapsedMs(): number {
    return paused ? elapsedMs : elapsedMs + (Date.now() - segmentStartedAt);
  }

  function render(): void {
    stopTicker();
    root.replaceChildren(view());
  }

  // -------------------------------------------------------------------------
  // Felvétel
  // -------------------------------------------------------------------------

  function startRecording(): void {
    const Recognizer = speechRecognitionCtor();
    if (!Recognizer) {
      manual = true;
      render();
      return;
    }

    transcript = '';
    errorMessage = '';
    elapsedMs = 0;
    paused = false;
    state = 'recording';
    segmentStartedAt = Date.now();
    render();
    attachRecognizer(Recognizer);
  }

  function attachRecognizer(Recognizer: SpeechRecognitionCtor): void {
    const rec = new Recognizer();
    rec.lang = 'hu-HU';
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (event) => {
      if (recognition !== rec) return;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        // A köztes találatot nem tesszük el: a végleges úgyis felülírja.
        if (!result || !result.isFinal) continue;
        const text = result[0]?.transcript ?? '';
        if (text.trim().length === 0) continue;
        transcript = transcript.length > 0 ? `${transcript} ${text.trim()}` : text.trim();
      }
    };

    rec.onerror = (event) => {
      if (recognition !== rec) return;
      recognition = null;
      // Ha már van átiratunk, nem dobjuk el: a hibaállapot megtartja.
      errorMessage = speechErrorMessage(event.error);
      state = 'error';
      render();
    };

    rec.onend = () => {
      if (recognition !== rec) return;
      // Szünet és kézi leállítás esetén nem itt döntünk a folytatásról.
      if (paused || state !== 'recording') return;
      recognition = null;
      finishRecording();
    };

    recognition = rec;
    try {
      rec.start();
    } catch {
      recognition = null;
      errorMessage = speechErrorMessage(undefined);
      state = 'error';
      render();
    }
  }

  function pauseRecording(): void {
    if (state !== 'recording' || paused) return;
    paused = true;
    elapsedMs += Date.now() - segmentStartedAt;
    recognition?.stop();
    recognition = null;
    render();
  }

  function resumeRecording(): void {
    if (state !== 'recording' || !paused) return;
    const Recognizer = speechRecognitionCtor();
    if (!Recognizer) return;
    paused = false;
    segmentStartedAt = Date.now();
    render();
    attachRecognizer(Recognizer);
  }

  function stopRecording(): void {
    if (state !== 'recording') return;
    if (!paused) elapsedMs += Date.now() - segmentStartedAt;
    paused = true;
    const rec = recognition;
    recognition = null;
    rec?.stop();
    finishRecording();
  }

  function cancelRecording(): void {
    const rec = recognition;
    recognition = null;
    paused = true;
    rec?.abort();
    reset();
  }

  function finishRecording(): void {
    stopTicker();
    void submit(transcript);
  }

  // -------------------------------------------------------------------------
  // Feldolgozás
  // -------------------------------------------------------------------------

  async function submit(text: string): Promise<void> {
    transcript = text;
    if (text.trim().length === 0) {
      errorMessage =
        t('ai.emptyTranscript');
      state = 'error';
      render();
      return;
    }

    const token = (submitToken += 1);
    state = 'processing';
    render();

    try {
      const result = await options.onSubmit(text);
      if (token !== submitToken) return;
      const normalized = normalizeDraft(result);
      if (normalized.tasks.length === 0) {
        errorMessage =
          t('ai.splitFailed');
        state = 'error';
        render();
        return;
      }
      draft = normalized;
      state = 'preview';
      render();
    } catch (error) {
      if (token !== submitToken) return;
      errorMessage =
        error instanceof Error && error.message.length > 0
          ? error.message
          : t('ai.splitFailed');
      state = 'error';
      render();
    }
  }

  function reset(): void {
    submitToken += 1;
    stopTicker();
    state = 'idle';
    manual = !supported;
    transcript = '';
    errorMessage = '';
    elapsedMs = 0;
    paused = false;
    draft = { title: '', tasks: [] };
    render();
  }

  // -------------------------------------------------------------------------
  // Párbeszédablakok
  // -------------------------------------------------------------------------

  function openTranscriptDialog(): void {
    const field = textField({
      label: t('ai.transcript'),
      required: true,
      multiline: true,
      value: transcript,
      hint: t('ai.transcriptHint'),
    });

    openDialog({
      title: t('ai.transcriptTitle'),
      width: '620px',
      body: () => h('div', { class: 'col g16' }, field.element),
      footer: (close) => [
        h('span', { class: 'grow' }),
        h('button', {
          class: 'btn btn-secondary',
          type: 'button',
          text: t('general.cancel'),
          onClick: close,
        }),
        h('button', {
          class: 'btn btn-primary',
          type: 'button',
          text: t('ai.processShort'),
          onClick: () => {
            if (!validateAll([field])) return;
            close();
            void submit(field.input.value);
          },
        }),
      ],
    });
  }

  function openTaskDialog(index: number): void {
    const row = draft.tasks[index];
    if (!row) return;

    const title = textField({
      label: t('task.projectTitle'),
      required: true,
      value: row.title,
      maxLength: 120,
    });
    const priority = selectField({
      label: t('task.priority'),
      required: true,
      value: row.priority,
      choices: priorityChoices,
    });

    openDialog({
      title: `${index + 1}. feladat szerkesztése`,
      width: '520px',
      body: () => h('div', { class: 'col g16' }, title.element, priority.element),
      footer: (close) => [
        h('span', { class: 'grow' }),
        h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
        h('button', {
          class: 'btn btn-primary',
          type: 'button',
          text: t('general.save'),
          onClick: () => {
            if (!validateAll([title, priority])) return;
            draft.tasks[index] = {
              title: title.input.value.trim(),
              priority: priority.input.value as TaskPriority,
            };
            close();
            render();
          },
        }),
      ],
    });
  }

  /// "Mind szerkesztése": soronként egy feladat, gyors átrendezéshez.
  function openBulkDialog(): void {
    const field = textField({
      label: t('ai.taskLines'),
      required: true,
      multiline: true,
      value: draft.tasks.map((t) => t.title).join('\n'),
      hint: t('ai.taskLinesHint'),
    });

    openDialog({
      title: t('ai.editAll'),
      width: '620px',
      body: () => h('div', { class: 'col g16' }, field.element),
      footer: (close) => [
        h('span', { class: 'grow' }),
        h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
        h('button', {
          class: 'btn btn-primary',
          type: 'button',
          text: t('general.save'),
          onClick: () => {
            if (!validateAll([field])) return;
            const lines = field.input.value
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0);
            draft.tasks = lines.map((line, index) => ({
              title: line,
              priority: draft.tasks[index]?.priority ?? 'mid',
            }));
            close();
            render();
          },
        }),
      ],
    });
  }

  // -------------------------------------------------------------------------
  // Nézetek
  // -------------------------------------------------------------------------

  function header(): HTMLElement {
    const steps: { key: AiCaptureState; label: string }[] = [
      { key: 'idle', label: t('ai.stageIdle') },
      { key: 'recording', label: t('ai.stageRecording') },
      { key: 'processing', label: t('ai.stageProcessing') },
      { key: 'preview', label: t('ai.stageResult') },
    ];
    const activeKey: AiCaptureState = state === 'error' ? 'idle' : state;

    return h(
      'header',
      {
        class: 'row between g12 wrap',
        style: { padding: '12px 16px', borderBottom: '1px solid var(--border)' },
      },
      h('h2', { class: 'h3', text: t('ai.webTitle') }),
      h(
        'div',
        { class: 'row g6 wrap', 'aria-label': t('ai.stepsLabel') },
        ...steps.map((step) =>
          h('span', {
            class: `chip${step.key === activeKey ? ' on' : ''}`,
            text: step.label,
            'aria-current': step.key === activeKey ? 'step' : undefined,
          }),
        ),
      ),
    );
  }

  function manualCard(): HTMLElement {
    const field = textField({
      label: t('ai.transcriptLabel'),
      required: true,
      multiline: true,
      value: transcript,
      placeholder: t('ai.projectPlaceholder'),
      hint: supported
        ? t('ai.typeHint')
        : t('ai.noRecognition'),
    });

    field.input.addEventListener('input', () => {
      transcript = field.input.value;
    });

    return h(
      'div',
      { class: 'card pad20 col g12' },
      h('span', { class: 'label', text: t('ai.textCapture') }),
      field.element,
      h(
        'div',
        { class: 'row g6' },
        supported
          ? h('button', {
              class: 'btn btn-ghost',
              type: 'button',
              text: t('ai.recordAnyway'),
              onClick: () => {
                manual = false;
                render();
              },
            })
          : null,
        h('span', { class: 'grow' }),
        h('button', {
          class: 'btn btn-primary',
          type: 'button',
          text: t('ai.process'),
          onClick: () => {
            if (!validateAll([field])) return;
            void submit(field.input.value);
          },
        }),
      ),
    );
  }

  function idleCard(): HTMLElement {
    return h(
      'div',
      { class: 'card pad20 col g12' },
      h('span', { class: 'label', text: t('ai.stageIdle') }),
      h('span', {
        class: 'body-sm ts',
        text:
          t('ai.idleHint'),
      }),
      h(
        'button',
        {
          class: 'btn btn-primary btn-lg',
          type: 'button',
          style: { width: '100%' },
          onClick: startRecording,
        },
        h('span', { class: 'ic' }, icon(micPath, 18)),
        h('span', { text: t('ai.startRecording') }),
      ),
      h('button', {
        class: 'btn btn-ghost btn-sm',
        type: 'button',
        text: t('ai.typeInstead'),
        onClick: () => {
          manual = true;
          render();
        },
      }),
    );
  }

  function recordingCard(): HTMLElement {
    // Látható jelzés, hogy hall-e a mikrofon: a sávok magassága menet közben
    // változik, szünetben megáll. A szöveges állapotot az `aria-live` mondja.
    const bars = Array.from({ length: 32 }, () =>
      h('span', {
        style: {
          width: '4px',
          borderRadius: '2px',
          height: '8px',
          background: 'var(--accent)',
          opacity: '0.7',
        },
      }),
    );

    const clock = h('span', {
      class: 'mono w6',
      style: { fontSize: '20px' },
      text: formatClock(currentElapsedMs()),
    });

    ticker = window.setInterval(() => {
      // Ha a képernyő már nincs a DOM-ban, az időzítő sem kell.
      if (!root.isConnected) {
        stopTicker();
        return;
      }
      clock.textContent = formatClock(currentElapsedMs());
      if (paused) return;
      for (const bar of bars) {
        bar.style.height = `${8 + Math.round(Math.random() * 46)}px`;
      }
    }, 140);

    return h(
      'div',
      { class: 'card pad20 col g12' },
      h('span', { class: 'label', text: t('ai.stageRecording') }),
      h('span', {
        class: 'body-sm ts',
        text: paused
          ? t('ai.pausedHint')
          : t('ai.recordingHint'),
      }),
      h(
        'div',
        { class: 'row g4', style: { height: '60px', alignItems: 'center' }, 'aria-hidden': 'true' },
        ...bars,
      ),
      h(
        'div',
        { class: 'row g12' },
        clock,
        h(
          'span',
          { class: paused ? 'badge st-wait' : 'badge st-rej', role: 'status' },
          h('span', { class: 'dot' }),
          h('span', { text: paused ? t('ai.pausedBadge') : t('ai.recordingBadge') }),
        ),
        h('span', { class: 'grow' }),
        h('button', {
          class: 'btn btn-secondary btn-sm',
          type: 'button',
          text: paused ? t('general.continue') : t('ai.pause'),
          onClick: () => (paused ? resumeRecording() : pauseRecording()),
        }),
        h('button', {
          class: 'btn btn-primary btn-sm',
          type: 'button',
          text: t('ai.stop'),
          onClick: stopRecording,
        }),
      ),
      h('button', {
        class: 'btn btn-ghost btn-sm',
        type: 'button',
        text: t('ai.discardRecording'),
        onClick: () => {
          cancelRecording();
          options.onCancel?.();
        },
      }),
    );
  }

  function processingCard(): HTMLElement {
    const fill = h('i', { style: { width: '8%' } });
    let percent = 8;

    ticker = window.setInterval(() => {
      if (!root.isConnected) {
        stopTicker();
        return;
      }
      // Nem tudjuk, meddig tart: a sáv 92%-ig kúszik, a végét az eredmény hozza.
      percent = Math.min(92, percent + 3);
      fill.style.width = `${percent}%`;
    }, 400);

    return h(
      'div',
      { class: 'card pad20 col g12' },
      h('span', { class: 'label', text: t('ai.stageProcessing') }),
      h('span', {
        class: 'body-sm ts',
        text: t('ai.processingHint'),
      }),
      h(
        'div',
        { class: 'row g12' },
        h('span', { class: 'ring', 'aria-hidden': 'true' }),
        h(
          'div',
          { class: 'col g6 grow' },
          h('div', { class: 'prog' }, fill),
          h('span', {
            class: 'caption',
            role: 'status',
            text: t('ai.transcriptReady'),
          }),
        ),
      ),
    );
  }

  function errorCard(): HTMLElement {
    return h(
      'div',
      { class: 'card pad20 col g12' },
      h('span', { class: 'label', text: '5 · Hiba' }),
      h('span', {
        class: 'body-sm ts',
        text: t('ai.rawTranscriptKept'),
      }),
      h(
        'div',
        {
          class: 'row g12',
          role: 'alert',
          style: {
            alignItems: 'flex-start',
            padding: '12px',
            borderRadius: 'var(--r-md)',
            background: 'var(--err-soft)',
          },
        },
        h('span', { class: 'ic', style: { color: 'var(--err)' } }, icon(icons.warning, 16)),
        h('span', { class: 'body-sm', style: { color: 'var(--err)' }, text: errorMessage }),
      ),
      transcript.trim().length > 0
        ? h(
            'div',
            { class: 'col g4' },
            h('span', { class: 'label', text: t('ai.transcript') }),
            h('span', { class: 'body-sm ts', text: transcript }),
          )
        : null,
      h(
        'div',
        { class: 'row g6 wrap' },
        h('button', {
          class: 'btn btn-secondary btn-sm',
          type: 'button',
          text: t('ai.editTranscript'),
          onClick: openTranscriptDialog,
        }),
        h('button', {
          class: 'btn btn-primary btn-sm',
          type: 'button',
          text: supported ? t('ai.recordAgain') : t('ai.restart'),
          onClick: () => (supported ? startRecording() : reset()),
        }),
      ),
    );
  }

  function taskRow(task: AiTaskDraft, index: number): HTMLElement {
    return h(
      'div',
      {
        class: 'row g12',
        style: {
          padding: '9px 10px',
          borderRadius: 'var(--r-sm)',
          border: '1px solid var(--border)',
        },
      },
      h('span', { class: 'mono caption', style: { width: '22px' }, text: `${index + 1}.` }),
      h('span', { class: 'body-sm grow', text: task.title }),
      priorityTag(task.priority),
      h(
        'button',
        {
          class: 'btn btn-ghost btn-icon btn-sm',
          type: 'button',
          'aria-label': `${index + 1}. feladat szerkesztése`,
          onClick: () => openTaskDialog(index),
        },
        icon(editPath, 14),
      ),
      h(
        'button',
        {
          class: 'btn btn-ghost btn-icon btn-sm',
          type: 'button',
          'aria-label': `${index + 1}. feladat elvetése`,
          onClick: () => {
            draft.tasks.splice(index, 1);
            render();
          },
        },
        icon(dropPath, 14),
      ),
    );
  }

  function previewPanel(): HTMLElement {
    const title = textField({
      label: 'Projekt neve',
      required: true,
      value: draft.title,
      maxLength: 120,
    });
    title.input.addEventListener('input', () => {
      draft.title = title.input.value;
    });

    return h(
      'div',
      { class: 'col g12', style: { minWidth: '0' } },
      h(
        'div',
        { class: 'col', style: { gap: '2px' } },
        h('h3', { class: 'h3', text: t('ai.resultTitle') }),
        h('span', {
          class: 'caption',
          text: t('ai.resultHint'),
        }),
      ),
      h(
        'div',
        { class: 'panel', style: { overflow: 'hidden' } },
        h(
          'div',
          {
            class: 'row g6',
            style: { padding: '12px 14px', borderBottom: '1px solid var(--border)' },
          },
          h('span', { class: 'body-sm w6', text: 'Javasolt projekt' }),
          h('span', { class: 'badge st-wait', text: t('ai.awaitingReview') }),
          h('span', { class: 'grow' }),
          h('button', {
            class: 'btn btn-ghost btn-sm',
            type: 'button',
            text: t('ai.editAll'),
            onClick: openBulkDialog,
          }),
        ),
        h(
          'div',
          { class: 'col g12', style: { padding: '14px' } },
          title.element,
          ...draft.tasks.map((task, index) => taskRow(task, index)),
          draft.tasks.length === 0
            ? h('span', {
                class: 'body-sm ts',
                text: t('ai.allDiscarded'),
              })
            : null,
          h('span', {
            class: 'caption',
            text:
              t('ai.disclaimer'),
          }),
        ),
      ),
      h(
        'div',
        { class: 'row g6' },
        h('button', {
          class: 'btn btn-ghost',
          type: 'button',
          text: t('ai.discard'),
          onClick: () => {
            reset();
            options.onCancel?.();
          },
        }),
        h('button', {
          class: 'btn btn-secondary',
          type: 'button',
          text: t('ai.editTranscript'),
          onClick: openTranscriptDialog,
        }),
        h('span', { class: 'grow' }),
        h('span', {
          class: 'caption',
          role: 'status',
          text: `${draft.tasks.length} feladat jön létre`,
        }),
        h('button', {
          class: 'btn btn-primary',
          type: 'button',
          text: t('ai.createProject'),
          disabled: draft.tasks.length === 0 ? 'disabled' : undefined,
          onClick: () => {
            if (!title.validate()) return;
            if (draft.tasks.length === 0) return;
            options.onCreate({
              title: title.input.value.trim(),
              tasks: draft.tasks.map((task) => ({ ...task })),
            });
          },
        }),
      ),
    );
  }

  function leftColumn(): HTMLElement {
    const cards: (HTMLElement | null)[] = [];

    if (state === 'recording') {
      cards.push(recordingCard());
    } else if (state === 'processing') {
      cards.push(processingCard());
    } else if (state === 'error') {
      cards.push(errorCard());
      if (manual) cards.push(manualCard());
    } else {
      // Készenlét és előnézet: innen indul az (újra)felvétel.
      cards.push(manual ? manualCard() : idleCard());
    }

    return h(
      'div',
      {
        class: 'col g16',
        style: { width: '420px', maxWidth: '100%', flex: 'none', minWidth: '0' },
      },
      ...cards,
    );
  }

  function rightColumn(): HTMLElement {
    if (state === 'preview') return previewPanel();

    return h(
      'div',
      { class: 'col g12 grow', style: { minWidth: '0' } },
      h(
        'div',
        { class: 'col', style: { gap: '2px' } },
        h('h3', { class: 'h3', text: t('ai.resultTitle') }),
        h('span', {
          class: 'caption',
          text: t('ai.resultPlaceholderHint'),
        }),
      ),
      h(
        'div',
        { class: 'panel pad20 col g12' },
        h('span', {
          class: 'body-sm ts',
          text:
            state === 'processing'
              ? t('ai.suggestionPending')
              : t('ai.noSuggestionYet'),
        }),
      ),
    );
  }

  function view(): HTMLElement {
    return h(
      'div',
      { class: 'col', style: { gap: '0' } },
      header(),
      h(
        'div',
        { class: 'row g16 wrap', style: { padding: '20px', alignItems: 'flex-start' } },
        leftColumn(),
        rightColumn(),
      ),
    );
  }

  render();
  return root;
}
